export const LIVE_DRAWING_API_VERSION = "2";
export const AI_DRAWING_IDLE_MS = 2 * 60_000;
export const AI_DRAWING_EXPIRY_MS = 10 * 60_000;
export const LIVE_DRAWING_PAGE_BYTES = 24 * 1024;
/** Keep page text below common clients' model-visible truncation threshold. */
export const LIVE_DRAWING_PAGE_TARGET_BYTES = 16 * 1024;
export const LIVE_DRAWING_RESULT_BYTES = 64 * 1024;
/** Live relay/cache budget, independent of the larger cloud file allowance. */
export const MAX_LIVE_SCENE_SIZE_BYTES = 4 * 1024 * 1024;
export const LIVE_DRAWING_OUTLINE_DEFAULT_GAP = 100;
export const LIVE_DRAWING_OUTLINE_MAX_GAP = 10_000;
/** Rendered export (drawing_export) contract; see design §2.8. */
export const LIVE_DRAWING_EXPORT_FORMATS = ["png", "svg"];
export const LIVE_DRAWING_EXPORT_DEFAULTS = {
    format: "png",
    scale: 2,
    padding: 24,
    background: true,
    darkMode: false,
};
export const LIVE_DRAWING_EXPORT_MIN_SCALE = 1;
export const LIVE_DRAWING_EXPORT_MAX_SCALE = 4;
export const LIVE_DRAWING_EXPORT_MAX_PADDING = 200;
export const LIVE_DRAWING_EXPORT_MAX_ELEMENT_IDS = 200;
export const LIVE_DRAWING_EXPORT_MAX_PIXELS = 16_777_216;
export const LIVE_DRAWING_EXPORT_MAX_SIDE = 8192;
/**
 * Keeps a resources/read inside the 8 MiB cross-replica relay envelope: the bytes
 * are base64 in the message, which the relay encrypts and base64-encodes again.
 */
export const LIVE_DRAWING_EXPORT_MAX_BYTES = 4 * 1024 * 1024;
export const LIVE_DRAWING_EXPORT_TTL_MS = 10 * 60_000;
export const LIVE_DRAWING_EXPORT_RENDER_TIMEOUT_MS = 20_000;
export const LIVE_DRAWING_EXPORT_URI_PREFIX = "excalidraw-ms://exports/";
/** Inline PNG preview attached to drawing_export results as an MCP image item. */
export const LIVE_DRAWING_EXPORT_INLINE_DEFAULTS = { inline: true, inlineWidth: 800 };
export const LIVE_DRAWING_EXPORT_INLINE_MIN_WIDTH = 64;
export const LIVE_DRAWING_EXPORT_INLINE_MAX_WIDTH = 2048;
/**
 * Largest raw preview PNG. Its base64 (4/3) plus the duplicated JSON text and
 * structuredContent, the resource_link and the JSON-RPC envelope must stay inside
 * the 64 KiB tool-result budget; the router also measures the final reply.
 */
export const LIVE_DRAWING_EXPORT_INLINE_MAX_BYTES = 39 * 1024;
export const LIVE_DRAWING_BBOX_MODES = ["intersects", "contains"];
/** Server-owned recovery text per exact cause; the wire carries only the reason key. */
export const LIVE_DRAWING_REASON_GUIDANCE = {
    CREDENTIAL_SIGN_IN_REQUIRED: {
        nextAction: "ask_user",
        hint: "The Graph credential exchange requires renewed sign-in or consent. Ask the user to sign in again in the MCP client, then retry once. If it persists, stop and report the requestId.",
    },
    CREDENTIAL_LIFETIME_INSUFFICIENT: {
        nextAction: "ask_user",
        hint: "The returned Graph credential has no usable lifetime after the safety margin. Ask the user to sign in again in the MCP client and retry once. If it repeats, stop and report the requestId; do not recreate the drawing.",
    },
    CREDENTIAL_CONFIGURATION_INVALID: {
        nextAction: "stop",
        hint: "The backend Graph credential configuration or tenant binding is invalid. Report the requestId through the service support route; do not recreate the drawing.",
    },
    CREDENTIAL_MANAGED_IDENTITY_FAILED: {
        nextAction: "wait_then_retry",
        hint: "The backend could not acquire its managed-identity credential. Wait briefly and retry once; if it persists, stop and report the requestId.",
    },
    CREDENTIAL_EXCHANGE_UNAVAILABLE: {
        nextAction: "wait_then_retry",
        hint: "The Graph credential exchange dependency is temporarily unavailable. Wait briefly and retry once; if it persists, stop and report the requestId.",
    },
    CREDENTIAL_EXCHANGE_FAILED: {
        nextAction: "stop",
        hint: "Graph credential acquisition failed before drawing access could continue. Report the requestId through the service support route; this is not evidence that the room is missing.",
    },
    CREDENTIAL_RESPONSE_INVALID: {
        nextAction: "stop",
        hint: "The Graph credential response did not contain a usable access token. Report the requestId through the service support route.",
    },
    CREDENTIAL_EXPIRY_MISSING: {
        nextAction: "stop",
        hint: "The Graph credential response did not provide a verified expiry. Report the requestId through the service support route; do not bypass credential validation.",
    },
    CREDENTIAL_EXPIRY_INVALID: {
        nextAction: "stop",
        hint: "The Graph credential response provided an invalid expiry. Report the requestId through the service support route; do not bypass credential validation.",
    },
    STALE_CONTEXT_EPOCH: {
        nextAction: "read_then_retry",
        hint: "The revision comes from an earlier context state. Call drawing_read and use the revision it returns.",
    },
    REVISION_FROM_FUTURE: {
        nextAction: "read_then_retry",
        hint: "The revision is newer than anything observed. Use only a revision returned by drawing_read or a mutation receipt.",
    },
    REVISION_NOT_CURRENT: {
        nextAction: "read_then_retry",
        hint: "This operation needs the exact current revision. Call drawing_read and reconsider against the fresh scene. For drawing_replace, rebuild the complete replacement scene from that snapshot; then resubmit with its revision and a new operationId.",
    },
    STALE_PRECONDITION: {
        nextAction: "read_then_retry",
        hint: "The listed elements changed since you read them. Call drawing_read, rebuild the patch and preconditions from the fresh fingerprints, and use a new operationId.",
    },
    MISSING_PRECONDITION: {
        nextAction: "fix_arguments",
        hint: "Add a precondition with the drawing_read or drawing_find fingerprint for each listed element ID: the elements you touch, their bound text and arrows, group members, and the frame of a framed element. If the list is long or truncated, use updateWhere or deleteWhere with a drawing_find selector (idPrefix, frameId) and ifUnchangedSince instead; the server supplies those fingerprints. New add IDs need no precondition.",
    },
    CHANGED_SINCE_REVISION: {
        nextAction: "read_then_retry",
        hint: "The listed elements matched by a selector operation, or their dependents, changed after ifUnchangedSince. Nothing was applied. Run drawing_find again, review the changes, and resend with its revision and a new operationId.",
    },
    SELECTOR_NO_MATCH: {
        nextAction: "fix_arguments",
        hint: "The deleteWhere or updateWhere selector matched no element. Check it with drawing_find, or remove the operation.",
    },
    FIND_CURSOR_STALE: {
        nextAction: "read_then_retry",
        hint: "The drawing or the query changed since this drawing_find cursor was issued. Run drawing_find again without a cursor.",
    },
    DUPLICATE_ELEMENT_ID: {
        nextAction: "fix_arguments",
        hint: "An add reuses an element ID that already exists. Choose fresh IDs or use an update operation, and use a new operationId.",
    },
    UNKNOWN_ELEMENT_ID: {
        nextAction: "read_then_retry",
        hint: "An update or delete targets an element ID that is not in the scene. Call drawing_read and use IDs from it.",
    },
    INVALID_PRECONDITION: {
        nextAction: "fix_arguments",
        hint: "Each precondition needs a unique non-empty elementId and an expectedFingerprint that is null or 64 lowercase hex characters.",
    },
    DUPLICATE_OPERATION_TARGET: {
        nextAction: "fix_arguments",
        hint: "One batch touches the same element more than once. Merge those operations into one per element.",
    },
    EMPTY_OPERATIONS: {
        nextAction: "fix_arguments",
        hint: "Send at least one operation.",
    },
    TOO_MANY_OPERATIONS: {
        nextAction: "fix_arguments",
        hint: "A batch holds at most 200 affected elements. Batch new elements together in add.elements. Split only at the element or 2 MiB request limit; read deltas when new fingerprints are needed.",
    },
    IMMUTABLE_FIELD: {
        nextAction: "fix_arguments",
        hint: "An update patch must not set id or type. Delete the element and add a new one to change its type.",
    },
    ELEMENT_TYPE_CHANGE: {
        nextAction: "fix_arguments",
        hint: "An element cannot change type. Delete it and add a new element instead.",
    },
    ELEMENT_SCHEMA_INVALID: {
        nextAction: "fix_arguments",
        hint: "The listed elements do not satisfy the advertised element schema; see issues for the failing paths. Add elements may omit defaultable fields, but every supplied field must be valid. Update patches are partial, must not set id or type, and receive no defaults; replacement scenes need complete elements.",
    },
    INTEGRITY_VIOLATION: {
        nextAction: "fix_arguments",
        hint: "The result would break a binding, container, frame, ordering or size rule; see issues for the rule and element. Fix the references or include their reciprocal updates in the same batch.",
    },
    PAYLOAD_TOO_LARGE: {
        nextAction: "fix_arguments",
        hint: "The request exceeds the 2 MiB limit or contains non-JSON values. Split it into smaller patches.",
    },
    SCENE_TOO_LARGE: {
        nextAction: "stop",
        hint: "The drawing would exceed the 4 MiB scene limit. Remove content or ask the user to split the drawing.",
    },
    SCENE_METADATA_CHANGED: {
        nextAction: "fix_arguments",
        hint: "drawing_replace changes elements only. Send appState and files exactly as read.",
    },
    OPERATION_ID_INVALID: {
        nextAction: "fix_arguments",
        hint: "Send a non-empty operationId of at most 100 characters, unique for each distinct operation.",
    },
    OPERATION_ID_REUSED: {
        nextAction: "fix_arguments",
        hint: "That operationId was used for different content. Reuse an operationId only to retry the identical request; otherwise send a new one.",
    },
    SCHEMA_VIOLATION: {
        nextAction: "fix_arguments",
        hint: "The arguments do not match the tool input schema; see issues for the JSON path and schema keyword. Correct them against the advertised schema.",
    },
    IMAGE_NOT_ALLOWED: {
        nextAction: "fix_arguments",
        hint: "Assistants cannot create images or change an image's file. Move, resize, group or delete existing images only.",
    },
    HIDDEN_ELEMENT: {
        nextAction: "fix_arguments",
        hint: "The target is an unsupported element that is preserved but not editable by assistants. Leave it untouched.",
    },
    READ_ONLY_ACCESS: {
        nextAction: "ask_user",
        hint: "This share code grants read-only access. Ask the user to share the drawing with edit access.",
    },
    OWNER_ONLY: {
        nextAction: "ask_user",
        hint: "Only the drawing owner can delete it. Ask the owner to delete it.",
    },
    TOO_MANY_CONTEXTS: {
        nextAction: "wait_then_retry",
        hint: "Too many open drawing contexts. Call drawing_close on contexts you no longer need, or wait for them to expire, then retry.",
    },
    RECEIPT_CAPACITY_REACHED: {
        nextAction: "reopen",
        hint: "This context retained its maximum 256 operation receipts. Call drawing_close, reopen the drawing, call drawing_read, and reconsider before editing.",
    },
    CONTEXT_IDLE_TIMEOUT: {
        nextAction: "reopen",
        hint: "The context closed after 10 minutes without drawing activity. Reopen the drawing, call drawing_read, and reconsider before editing.",
    },
    CONTEXT_CREDENTIAL_EXPIRED: {
        nextAction: "reopen",
        hint: "The delegated sign-in captured by this context expired. Reopen the drawing; if that fails with AUTH_REQUIRED, ask the user to sign in again.",
    },
    CONTEXT_AUTHORIZATION_LOST: {
        nextAction: "reopen",
        hint: "Re-checking drawing access for this context failed, so it closed. Reopen the drawing; if access is denied, ask the user to check sharing.",
    },
    CONTEXT_REVOKED: {
        nextAction: "ask_user",
        hint: "The AI share code for this context was revoked. Ask the user to share the drawing with AI again.",
    },
    CONTEXT_CLOSED: {
        nextAction: "reopen",
        hint: "This context was closed by drawing_close, drawing_delete, a failed open, or a server shutdown. Reopen the drawing if you still need it.",
    },
    LIVE_SYNC_DISCONNECTED: {
        nextAction: "reopen",
        hint: "The assistant's live connection to the drawing room dropped, so the context closed. Reopen once and check with drawing_find or drawing_read whether your earlier edits are present before re-adding anything; if it happens again, stop and tell the user the requestId.",
    },
    LIVE_SCENE_REJECTED: {
        nextAction: "reopen",
        hint: "A live update from another participant could not be accepted, so the context closed. Reopen once and check with drawing_find or drawing_read whether your earlier edits are present before re-adding anything; if it happens again, stop and tell the user the requestId.",
    },
    ROUTE_HANDLE_INVALID: {
        nextAction: "reopen",
        hint: "The handle could not be decoded by this server (for example after a key rotation or a different environment). Reopen the drawing with the user's code.",
    },
    ROUTE_RECONNECTING: {
        nextAction: "wait_then_retry",
        hint: "MCP routing is reconnecting; this request was not sent. Keep the same handle. Do not reopen or create another drawing context for this error. Wait briefly and retry once; if routing still fails, stop and report the requestId through the service support route.",
    },
    ROUTE_CONFIGURATION_INVALID: {
        nextAction: "stop",
        hint: "The backend MCP routing configuration is invalid. This request was not sent. Do not reopen or create another context; report the requestId through the service support route.",
    },
    ROUTE_RESPONSE_INVALID: {
        nextAction: "stop",
        hint: "MCP routing returned an invalid response, so the operation outcome is unknown. Keep the same handle. Do not reopen, create another context, or replay the mutation; report the requestId through the service support route.",
    },
    ROUTE_REPLICA_UNREACHABLE: {
        nextAction: "read_then_retry",
        hint: "MCP routing lost its connection; this does not establish context expiry. The operation outcome is unknown. Keep the same handle and do not reopen or create another context. Wait briefly, then call drawing_read with that handle and reconcile before reconsidering edits. Do not blindly replay a mutation. If routing still fails, stop and report the requestId through the service support route.",
    },
    ROUTE_TIMEOUT: {
        nextAction: "read_then_retry",
        hint: "MCP routing did not return a verified reply before its deadline; this does not establish context expiry. The operation may have been applied. Keep the same handle and do not reopen or create another context. Wait briefly, then call drawing_read with that handle and reconcile before reconsidering edits. Do not blindly replay a mutation. If routing still fails, stop and report the requestId through the service support route.",
    },
    EXPORT_SCOPE_NOT_FOUND: {
        nextAction: "read_then_retry",
        hint: 'The frameId or an elementIds entry is not a non-deleted element you can read. Find frames with drawing_find({"query":{"type":"frame"}}) or elements with drawing_find, then export with returned IDs.',
    },
    EXPORT_SCOPE_EMPTY: {
        nextAction: "fix_arguments",
        hint: "The export scope contains nothing to render. Choose a frame or elements with content, or omit frameId and elementIds to export the whole drawing.",
    },
    EXPORT_TOO_LARGE: {
        nextAction: "fix_arguments",
        hint: "The image would exceed 16,777,216 pixels, 8,192 px on a side or 4 MiB. Lower scale, or export a frame or fewer elements.",
    },
    EXPORT_RENDERER_BUSY: {
        nextAction: "wait_then_retry",
        hint: "The renderer is busy with other exports. Wait a few seconds, then retry once.",
    },
    EXPORT_RENDERER_STARTING: {
        nextAction: "wait_then_retry",
        hint: "The renderer on this server is still starting (the first export after a restart can take up to about 30 seconds). Wait a few seconds, then retry; it keeps starting meanwhile.",
    },
    EXPORT_RATE_LIMITED: {
        nextAction: "wait_then_retry",
        hint: "Too many exports in the last minute. Wait, then retry; reuse unchanged exports by comparing contentHash.",
    },
    EXPORT_RENDER_TIMEOUT: {
        nextAction: "fix_arguments",
        hint: "Rendering did not finish within 20 seconds. Lower scale or export a smaller frame or fewer elements.",
    },
    EXPORT_RENDER_FAILED: {
        nextAction: "wait_then_retry",
        hint: "The renderer failed unexpectedly. Retry once; if it fails again, stop and report the requestId.",
    },
    EXPORT_DEADLINE: {
        nextAction: "wait_then_retry",
        hint: "The export could not finish within this call's time budget because the server holding the context was busy. Retry once; if it happens again, try later.",
    },
    EXPORT_FILES_UNAVAILABLE: {
        nextAction: "wait_then_retry",
        hint: "Images in the export scope were added by a collaborator and their files are not saved yet. Wait until the drawing is saved (persistence saved in drawing_read), then retry.",
    },
    EXPORT_NOT_FOUND: {
        nextAction: "read_then_retry",
        hint: "The export expired (10 minutes) or its context closed. Call drawing_export again and read the new uri.",
    },
};
/** Public application error catalog, grouped by failure domain. */
export const LIVE_DRAWING_ERROR_CATALOG = {
    AUTH_REQUIRED: {
        category: "AUTHENTICATION",
        message: "Delegated drawing authorization has expired.",
        nextAction: "ask_user",
        hint: "Delegated authorization expired or is missing. Ask the user to sign in again, then reopen the drawing.",
    },
    CREDENTIAL_UNAVAILABLE: {
        category: "AUTHENTICATION",
        message: "A usable Graph credential could not be obtained.",
        nextAction: "stop",
        hint: "The backend could not obtain a usable Graph credential. Follow details.reason and report the requestId through the service support route if recovery fails.",
    },
    ACCESS_DENIED: {
        category: "AUTHORIZATION",
        message: "Drawing access is not permitted for this caller.",
        nextAction: "ask_user",
        hint: "This caller may not perform the operation on this drawing. Ask the user to check sharing and the AI share code.",
    },
    GRANT_NOT_FOUND: {
        category: "AUTHORIZATION",
        message: "No AI share code matches that value for this user. Ask the user to re-share the drawing.",
        nextAction: "ask_user",
        hint: "No active AI share code matches. Ask the user to share the drawing with AI again and give you the new code.",
    },
    GRANT_REVOKED: {
        category: "AUTHORIZATION",
        message: "The AI share code is no longer active.",
        nextAction: "ask_user",
        hint: "The AI share code was revoked. Ask the user to share the drawing again.",
    },
    DRAWING_CONTEXT_EXPIRED: {
        category: "CONTEXT",
        message: "Drawing context expired. Reopen the drawing and read it again.",
        nextAction: "reopen",
        hint: "Call drawing_open with the AI share code (or drawing_create), then drawing_read, and reconsider before editing. Do not replay old edits.",
    },
    MCP_ROUTING_FAILED: {
        category: "ROUTING",
        message: "MCP routing failed. Keep the same handle and do not reopen the drawing. This does not establish room unavailability or context expiry. See details.reason and details.hint.",
        nextAction: "read_then_retry",
        hint: "MCP routing failed; this does not establish room unavailability or context expiry. Keep the same handle and do not reopen or create another context. Follow details.reason when present. The operation outcome may be unknown: wait briefly, call drawing_read with the same handle, and reconcile before reconsidering edits. Do not blindly replay a mutation. If routing still fails, stop and report the requestId through the service support route.",
    },
    ROOM_UNAVAILABLE: {
        category: "COLLABORATION",
        message: "Live drawing is temporarily unavailable.",
        nextAction: "wait_then_retry",
        hint: "The native drawing room or peer is temporarily unavailable. Follow details.reason when present. Keep any existing handle and retry once after a short delay. If it persists, stop and report the requestId through the service support route.",
    },
    RESYNC_REQUIRED: {
        category: "SYNCHRONIZATION",
        message: "Read the drawing again before reconsidering edits.",
        nextAction: "read_then_retry",
        hint: "Call drawing_read and reconsider your edits against the fresh scene.",
    },
    REVISION_CONFLICT: {
        category: "SYNCHRONIZATION",
        message: "The request conflicts with the current live drawing state. See details.reason and details.hint.",
        nextAction: "read_then_retry",
        hint: "The request conflicts with the live drawing. Call drawing_read, rebuild the request from the fresh snapshot, and use a new operationId.",
    },
    IDEMPOTENCY_CONFLICT: {
        category: "SYNCHRONIZATION",
        message: "The operation ID was already used for a different operation.",
        nextAction: "fix_arguments",
        hint: "Use a new operationId unless this is an identical retry.",
    },
    INVALID_ARGUMENT: {
        category: "VALIDATION",
        message: "Arguments do not match the advertised drawing operation.",
        nextAction: "fix_arguments",
        hint: "Correct the arguments to match the tool input schema.",
    },
    INVALID_OPERATION: {
        category: "VALIDATION",
        message: "The drawing operation is invalid. See details.hint.",
        nextAction: "fix_arguments",
        hint: "Correct the operation; the same request will fail again.",
    },
    UNSUPPORTED_ASSET: {
        category: "VALIDATION",
        message: "Assistants cannot create images, change image files, or edit unsupported elements.",
        nextAction: "fix_arguments",
        hint: "Assistants cannot create images, change image files, or edit unsupported elements.",
    },
    PERSISTENCE_CONFLICT: {
        category: "PERSISTENCE",
        message: "Drawing storage changed. Read it again before reconsidering edits.",
        nextAction: "read_then_retry",
        hint: "Drawing storage changed underneath the live session. Call drawing_read and reconsider.",
    },
    PERSISTENCE_UNAVAILABLE: {
        category: "PERSISTENCE",
        message: "Drawing persistence is temporarily unavailable.",
        nextAction: "wait_then_retry",
        hint: "Storage is temporarily unavailable. Your live edits are retained; wait, then call drawing_read to confirm persistence.saved.",
    },
    RATE_LIMITED: {
        category: "CAPACITY",
        message: "Too many pending drawing operations.",
        nextAction: "wait_then_retry",
        hint: "Too many pending operations. Wait a few seconds, then retry.",
    },
    INTERNAL_ERROR: {
        category: "INTERNAL",
        message: "An unexpected server failure occurred. See details.hint and report the requestId.",
        nextAction: "stop",
        hint: "Stop and report the requestId through the service support route. The operation outcome may be unknown; retain any existing handle, do not reopen automatically and do not blindly replay mutations. This error does not establish room unavailability, routing failure or context expiry.",
    },
    MIGRATION_REQUIRED: {
        category: "COMPATIBILITY",
        message: "This tool has been retired. Use the supported drawing tools.",
        nextAction: "fix_arguments",
        hint: "Use drawing_open(code) or drawing_create(name), then drawing_read/patch/replace/close/delete with the returned handle.",
    },
};
/** Compatibility name for consumers of the original guidance table. */
export const LIVE_DRAWING_CODE_GUIDANCE = LIVE_DRAWING_ERROR_CATALOG;
export const LIVE_DRAWING_ERROR_LIST_LIMIT = 20;
const MAX_FACT_TEXT = 100;
const SAFE_FACT_TEXT = /^[\w.\-[\]:/@~]+$/;
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function safeText(value) {
    return typeof value === "string" &&
        value.length > 0 &&
        value.length <= MAX_FACT_TEXT &&
        SAFE_FACT_TEXT.test(value)
        ? value
        : undefined;
}
export function isLiveDrawingErrorReason(value) {
    return typeof value === "string" && Object.hasOwn(LIVE_DRAWING_REASON_GUIDANCE, value);
}
/** Drop everything outside the allowlist, cap lists, and refuse free-form text. */
export function sanitizeLiveDrawingErrorFacts(value) {
    if (!isRecord(value) || !isLiveDrawingErrorReason(value.reason))
        return undefined;
    const facts = { reason: value.reason };
    for (const key of ["baselineRevision", "currentRevision"]) {
        const revision = value[key];
        if (isRecord(revision) &&
            typeof revision.epoch === "string" &&
            revision.epoch.length > 0 &&
            revision.epoch.length <= 100 &&
            Number.isSafeInteger(revision.sequence) &&
            revision.sequence >= 0) {
            facts[key] = { epoch: revision.epoch, sequence: revision.sequence };
        }
    }
    if (value.applied === false)
        facts.applied = false;
    let truncated = value.truncated === true;
    if (Number.isSafeInteger(value.operationIndex) && value.operationIndex >= 0) {
        facts.operationIndex = value.operationIndex;
    }
    if (Array.isArray(value.elementIds)) {
        const ids = value.elementIds.map(safeText).filter((id) => !!id);
        if (ids.length > LIVE_DRAWING_ERROR_LIST_LIMIT)
            truncated = true;
        if (ids.length)
            facts.elementIds = ids.slice(0, LIVE_DRAWING_ERROR_LIST_LIMIT);
    }
    const field = safeText(value.field);
    if (field)
        facts.field = field;
    if (Array.isArray(value.issues)) {
        const issues = [];
        for (const raw of value.issues) {
            if (!isRecord(raw))
                continue;
            const code = safeText(raw.code);
            if (!code)
                continue;
            const issue = { code };
            const elementId = safeText(raw.elementId);
            const path = safeText(raw.path);
            if (elementId)
                issue.elementId = elementId;
            if (path)
                issue.path = path;
            issues.push(issue);
        }
        if (issues.length > LIVE_DRAWING_ERROR_LIST_LIMIT)
            truncated = true;
        if (issues.length)
            facts.issues = issues.slice(0, LIVE_DRAWING_ERROR_LIST_LIMIT);
    }
    if (truncated)
        facts.truncated = true;
    return facts;
}
/** Complete agent-facing details: reason-specific guidance, else the code's default. */
export function liveDrawingErrorDetails(code, facts) {
    const guidance = facts
        ? LIVE_DRAWING_REASON_GUIDANCE[facts.reason]
        : LIVE_DRAWING_CODE_GUIDANCE[code];
    return {
        ...facts,
        category: LIVE_DRAWING_ERROR_CATALOG[code].category,
        nextAction: guidance.nextAction,
        hint: guidance.hint,
    };
}
/** Stable JSON for scene fingerprints and exact retry-body comparison. */
export function canonicalSceneJson(value) {
    if (value === null || typeof value !== "object") {
        const encoded = JSON.stringify(value);
        if (encoded === undefined)
            throw new TypeError("Scene data must contain JSON values");
        return encoded;
    }
    if (Array.isArray(value))
        return `[${value.map(canonicalSceneJson).join(",")}]`;
    return `{${Object.keys(value)
        .filter((key) => value[key] !== undefined)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${canonicalSceneJson(value[key])}`)
        .join(",")}}`;
}
/** Content identity for save coordination, independent of aggregate element versions. */
export async function fingerprintCollabElements(elements) {
    const bytes = new TextEncoder().encode(canonicalSceneJson(elements));
    const hash = await globalThis.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
//# sourceMappingURL=liveDrawing.js.map